import BasemapGalleryVM from "@arcgis/core/widgets/BasemapGallery/BasemapGalleryViewModel.js";
import LocalBasemapsSource from "@arcgis/core/widgets/BasemapGallery/support/LocalBasemapsSource.js";
import "@arcgis/map-components/components/arcgis-layer-list";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Tab, TabList, TabPanel, Tabs } from "react-tabs";
import { setLayerListOpen } from "../../../store/appSlice";
import FloatingWindow from "../../genericComponents/FloatingWindow/FloatingWindow";

import LabelIcon from "../../../assets/label.svg?react";
import LayerlistIcon from "../../../assets/layerlist.svg?react";
import BackgroundIcon from "../../../assets/layerlist/background.svg?react";
import DetailsIcon from "../../../assets/layerlist/details.svg?react";
import FilterIcon from "../../../assets/layerlist/filter.svg?react";

import Basemap from "@arcgis/core/Basemap";
import VectorTileLayer from "@arcgis/core/layers/VectorTileLayer";
import Portal from "@arcgis/core/portal/Portal";
import { toast } from "react-toastify";
import { AppConfig } from "../../../AppConfig";
import { StoreState } from "../../../store/rootReducer";
import { triggerLayerlistRefresh } from "../../../store/triggersSlice";
import {
    applyLabelsToNFSLayers,
    applyNFSStatusFilter,
    getMapView,
    removeLabelsFromNFSLayers,
    removeNFSStatusFilter,
} from "../../../utils/arcgisUtils";
import { getConfigFromWebMap, getThemeMapsFromGroup } from "../../../utils/portalUtils";
import NfsButton from "../../genericComponents/NfsButton/NfsButton";
import LabelControl from "../LabelControl/LabelControl";
import LayerlistBackground from "./LayerlistBackground/LayerlistBackground";
import "./LayerlistControl.css";
import LayerlistDetails from "./LayerlistDetails/LayerlistDetails";
import { loadMapSettingsFromLocalStorage, storeMapSettingsInLocalStorage } from "../../../utils/layerlistUtils";
import { getMapSettings } from "../../../utils/localStorageUtils";
import FilterControl from "./FilterControl/FilterControl";
import type FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import type WebMap from "@arcgis/core/WebMap";

interface LayerlistControlProps {
    configWebMap: WebMap | null | undefined;
    setConfigWebMap: React.Dispatch<React.SetStateAction<WebMap | null | undefined>>;
    basemapGalleryVM: BasemapGalleryVM | null;
    setBasemapGalleryVM: React.Dispatch<React.SetStateAction<BasemapGalleryVM | null>>;
    themeMaps: WebMap[];
    setThemeMaps: React.Dispatch<React.SetStateAction<WebMap[]>>;
    portalBasemaps: Basemap[];
    setPortalBasemaps: React.Dispatch<React.SetStateAction<Basemap[]>>;
}

const LayerlistControl = (props: LayerlistControlProps) => {
    const dispatch = useDispatch();

    const [tabIndex, setTabIndex] = useState(0);
    const [enabledFields, setEnabledFields] = useState<string[]>([]);
    const [labelsEnabled, setLabelsEnabled] = useState<boolean>(false);
    const [hasAutoLoadedSettings, setHasAutoLoadedSettings] = useState(false);

    const [enabledNFSStatuses, setEnabledNFSStatuses] = useState<number[]>([]);
    const [filterEnabled, setFilterEnabled] = useState<boolean>(false);

    const layerlistRef = useRef(document.createElement("div"));
    const layerlistOpen = useSelector((state: StoreState) => state.app.layerListOpen);
    const defaultMinScales = useSelector((state: StoreState) => state.app.defaultMinScales);
    const viewLoaded = useSelector((state: StoreState) => state.app.viewLoaded);
    const showRemovedFeatures = useSelector((state: StoreState) => state.app.showRemovedFeatures);

    let tries = 0;
    const getLayerConfig = async (force?: boolean) => {
        if (!props.configWebMap || force) {
            const webmap = await getConfigFromWebMap("3efb9b0a1249411e89950c0789ceaa86");
            if (webmap) {
                props.setConfigWebMap(webmap);
            } else {
                tries++;
                if (tries > 5) {
                    toast.error("Kunne ikke hente kartlag fra webmap");
                    return;
                }
                setTimeout(() => {
                    getLayerConfig();
                }, 1000);
            }
        }
    };

    let themeTries = 0;
    const getThemeMapsConfig = async (force?: boolean) => {
        if (!props.configWebMap || force) {
            const webmaps = await getThemeMapsFromGroup("3c12ee1e42554817b7eb2703d1595a75");
            if (webmaps) {
                props.setThemeMaps(webmaps);
            } else {
                themeTries++;
                if (themeTries > 5) {
                    toast.error("Kunne ikke hente temakart fra portalen");
                    return;
                }
                setTimeout(() => {
                    getThemeMapsConfig();
                }, 1000);
            }
        }
    };

    const loadBasemapGallery = async (): Promise<BasemapGalleryVM | null> => {
        const view = getMapView();
        if (!view) {
            return null;
        }
        if (!props.basemapGalleryVM) {
            const portal = new Portal({
                url: AppConfig.ArcGIS.BaseUrl,
                authMode: "immediate",
            });

            if (!portal.loaded) {
                await portal.load();
            }
            const basemaps = await portal.queryItems({ query: 'group:"8a759448daea42c581236f6e7ae20b9a"' });
            const filteredBasemaps = basemaps.results
                .filter((b) => b.type === "Web Map" || b.type === "Basemap")
                .map((b) => new Basemap({ portalItem: b }));
            props.setPortalBasemaps(filteredBasemaps);
            const source = new LocalBasemapsSource({
                basemaps: filteredBasemaps,
            });

            const basemapGallery = new BasemapGalleryVM({
                view: view,
                source: source,
            });
            await basemapGallery.load();
            basemapGallery.items.forEach(async (b) => {
                await b.basemap.load();
            });
            props.setBasemapGalleryVM(basemapGallery);
            return basemapGallery;
        }
        return props.basemapGalleryVM;
    };

    const resetLayerSettings = async () => {
        const mapView = getMapView();
        if (!mapView?.map) return;

        for (const layer of AppConfig.Layers) {
            const mapLayer = mapView.map?.findLayerById(layer.Name) as FeatureLayer;
            if (mapLayer) {
                mapLayer.visible = layer.DefaultVisible;
                mapLayer.opacity = 1;

                mapLayer.minScale = defaultMinScales[layer.Name] ?? 70000;
            }
        }
        dispatch(triggerLayerlistRefresh());

        removeLabelsFromNFSLayers();
        setEnabledFields([]);
        setLabelsEnabled(false);

        removeNFSStatusFilter(showRemovedFeatures);
        setFilterEnabled(false);
        setEnabledNFSStatuses([]);

        const defaultBaseLayer = new VectorTileLayer({
            url: AppConfig.Basemaps.find((basemap) => basemap.Name === "Default")?.ServiceUrl,
        });
        await defaultBaseLayer.load();
        const defaultBasemap = new Basemap({
            baseLayers: [defaultBaseLayer],
            spatialReference: defaultBaseLayer.spatialReference,
        });

        if (props.basemapGalleryVM) {
            props.basemapGalleryVM.activeBasemap = defaultBasemap;
        }
    };

    const storeMapSettings = () => {
        storeMapSettingsInLocalStorage(enabledFields, labelsEnabled);
    };

    // The new label state is passed explicitly rather than read back from state, since the
    // state update has not been committed yet when we persist.
    const applyLabels = () => {
        setLabelsEnabled(true);
        applyLabelsToNFSLayers(enabledFields);
        storeMapSettingsInLocalStorage(enabledFields, true);
    };

    const removeLabels = () => {
        setLabelsEnabled(false);
        removeLabelsFromNFSLayers();
        storeMapSettingsInLocalStorage(enabledFields, false);
    };

    const restoreLabelSettings = (labels: string[], enabled: boolean) => {
        setEnabledFields(labels);
        setLabelsEnabled(enabled);
        if (enabled) {
            applyLabelsToNFSLayers(labels);
        } else {
            removeLabelsFromNFSLayers();
        }
    };

    const loadMapSettings = async () => {
        let basemapGalleryVM = props.basemapGalleryVM;
        if (!basemapGalleryVM) {
            basemapGalleryVM = await loadBasemapGallery();
            if (!basemapGalleryVM) {
                return;
            }
        }
        loadMapSettingsFromLocalStorage(props.themeMaps, props.configWebMap, restoreLabelSettings, basemapGalleryVM);
    };

    useEffect(() => {
        if (filterEnabled) {
            applyNFSStatusFilter(enabledNFSStatuses, showRemovedFeatures);
        } else {
            removeNFSStatusFilter(showRemovedFeatures);
        }
    }, [enabledNFSStatuses, filterEnabled, showRemovedFeatures]);

    useEffect(() => {
        if (!props.configWebMap) {
            getLayerConfig();
        }
        if (props.themeMaps.length === 0) {
            getThemeMapsConfig();
        }
        loadBasemapGallery();
    }, []);

    // Auto-load map settings once all required components are ready
    useEffect(() => {
        // Check if all required dependencies are loaded and we haven't auto-loaded yet
        if (!hasAutoLoadedSettings && viewLoaded && props.configWebMap && props.themeMaps.length > 0) {
            // Check if there are saved settings in localStorage
            const mapSettings = getMapSettings();
            if (mapSettings) {
                loadMapSettings();
                setHasAutoLoadedSettings(true);
            }
        }
    }, [hasAutoLoadedSettings, viewLoaded, props.configWebMap, props.themeMaps]);

    if (!layerlistOpen) {
        return null;
    }

    const calcHeightAndWidth = () => {
        const widthAndHeight = { x: 0, y: 0 };
        widthAndHeight.x = Math.max(window.innerWidth - Math.min(700, window.innerWidth - 40), 200);
        widthAndHeight.y = 40;
        return widthAndHeight;
    };

    return (
        <FloatingWindow
            closeWindow={() => dispatch(setLayerListOpen(false))}
            toolRef={layerlistRef}
            Icon={LayerlistIcon}
            title="Kartlag"
            className="layerlist-floating-window-container"
            contentClassName="layerlist-floating-window-content"
            defaultPosition={calcHeightAndWidth()}
            width={600}
        >
            <div className="layerlist-control">
                <Tabs
                    className="flex-col overflow-hidden"
                    selectedTabClassName="selected"
                    selectedIndex={tabIndex}
                    onSelect={(index) => setTabIndex(index)}
                >
                    <TabList className="layerlist-tab-list">
                        <Tab className="layerlist-tab-button">
                            <DetailsIcon />
                            Detaljer
                        </Tab>
                        <Tab className="layerlist-tab-button">
                            <BackgroundIcon />
                            Bakgrunn
                        </Tab>
                        <Tab className="layerlist-tab-button">
                            <LabelIcon />
                            Labels
                        </Tab>
                        <Tab className="layerlist-tab-button">
                            <FilterIcon />
                            Filter
                        </Tab>
                    </TabList>
                    <TabPanel className="layerlist-tab-panel">
                        <LayerlistDetails
                            configWebMap={props.configWebMap}
                            refreshConfigWebMap={() => getLayerConfig(true)}
                            onSettingsChanged={storeMapSettings}
                            nfsStatusFilterEnabled={filterEnabled && enabledNFSStatuses.length > 0}
                        />
                    </TabPanel>
                    <TabPanel className="layerlist-tab-panel">
                        <LayerlistBackground
                            basemapGalleryVM={props.basemapGalleryVM}
                            themeMaps={props.themeMaps}
                            refreshThemeMaps={() => getThemeMapsConfig(true)}
                            loadBasemapGallery={loadBasemapGallery}
                            onSettingsChanged={storeMapSettings}
                        />
                    </TabPanel>
                    <TabPanel className="layerlist-tab-panel">
                        <LabelControl
                            enabledFields={enabledFields}
                            setEnabledFields={setEnabledFields}
                            labelsEnabled={labelsEnabled}
                            onApplyLabels={applyLabels}
                            onRemoveLabels={removeLabels}
                        />
                    </TabPanel>
                    <TabPanel className="layerlist-tab-panel">
                        <FilterControl
                            enabledNFSStatuses={enabledNFSStatuses}
                            setEnabledNFSStatus={setEnabledNFSStatuses}
                            filterEnabled={filterEnabled}
                            setFilterEnabled={setFilterEnabled}
                        />
                    </TabPanel>
                </Tabs>
                <NfsButton outlined onClick={resetLayerSettings}>
                    Tilbakestill alle kartinnstillinger
                </NfsButton>
            </div>
        </FloatingWindow>
    );
};

export default LayerlistControl;
